import React, { useMemo, useState, useRef, useEffect, useCallback } from 'react';
import PropTypes from 'prop-types';
import { useSortable } from '@dnd-kit/sortable';
import HierarchyNodeContent from './HierarchyNodeContent';
import {
    IconChevronRight,
    IconDropDownMo,
    IconDrag,
    IconCondition,
} from 'svgIcons/MotionPortalIcons';
import { DROP_POSITION, DROP_THRESHOLD } from '../constants';
import { Checkbox } from 'sharedComponents/Checkbox';
import { NODE_TYPE, VALID_TEXT_REGEX } from '../../../PowertrainTools/PowertrainConfigurationV2/Hierarchy/constants';
import { translate } from 'helpers/translateHelper';
import { MAX_NODE_NAME_LENGTH } from 'helpers/constants';
import Tippy from '@tippy.js/react';


const HierarchyNode = React.memo(({
    node,
    level,
    isSelected,
    isExpanded,
    isDragging,
    isOver,
    dropPosition,
    indent,
    showExpandIcon,
    showCheckbox,
    onToggleExpand,
    onNodeClick,
    onContextMenu,
    renderNode,
    renderIcon,
    renderActions,
    dragEnabled,
    getNodeKey,
    isNodeSelectable,
    style,
    editMode,
    isEditing,
    showDragHandle,
    onSaveNewNode,
    onCancelNewNode,
    onSetDropPosition,
    draggedItems,
    childNodeCount,
    showDragPreview
}) => {
    const nodeKey = getNodeKey(node);
    const hasChildren = node?.hasChildren || node?.children && node?.children?.length > 0;
    const hasVisibleChildren = Boolean(node?.children && node?.children?.length > 0);
    const canExpand = editMode ? hasVisibleChildren : hasChildren;
    const selectable = isNodeSelectable(node);

    // Local state for inline editing
    const [editValue, setEditValue] = useState(node.name || '');
    const [editError, setEditError] = useState(null);
    const inputRef = useRef(null);

    // Focus input when editing starts
    useEffect(() => {
        if ((isEditing || node.inputEle) && inputRef.current) {
            inputRef.current.focus();
            inputRef.current.select();
        }
    }, [isEditing, node.inputEle]);

    // Reset edit value when node name changes or editing stops
    useEffect(() => {
        setEditValue(node.name || '');
        setEditError(null);
    }, [node.name, isEditing]);

    // Setup drag and drop with @dnd-kit
    const isDraggable = dragEnabled && node.draggable !== false;
    const {
        attributes,
        listeners,
        setNodeRef,
        transition,
        isDragging: isSortableDragging,
    } = useSortable({
        id: nodeKey,
        disabled: !isDraggable,
        data: {
            node,
            level,
        },
    });

    const sortableStyle = {
        transition,
        opacity: isSortableDragging ? 0.5 : 1,
    };

    // Handle expand/collapse toggle
    const handleExpandToggle = useCallback((e) => {
        e.stopPropagation();
        if (canExpand) {
            onToggleExpand(node);
        }
    }, [canExpand, onToggleExpand, node]);

    // Handle node click
    const handleClick = useCallback((e) => {
        if (isEditing || node.inputEle) {
            return; // Don't trigger click while editing or for input nodes
        }
        if (selectable) {
            onNodeClick(node, e);
        } else if (hasChildren) {
            // Non-selectable parent nodes toggle expand/collapse on row click
            onToggleExpand(node);
        }
    }, [isEditing, node.inputEle, selectable, onNodeClick, node, hasChildren, onToggleExpand]);

    // Handle context menu
    const handleContextMenu = useCallback((e) => {
        if (onContextMenu) {
            onContextMenu(node, e);
        }
    }, [onContextMenu, node]);

    // Handle edit input change
    const handleEditChange = useCallback((e) => {
        e.stopPropagation(); // Prevent event from bubbling up
        const val = e.target.value;
        setEditValue(val);
        if (val && !VALID_TEXT_REGEX.test(val)) {
            setEditError(translate('ABB.Powertrain.Frontend.locationNameSpecialCharErrorMessage'));
        } else {
            setEditError(null);
        }
    }, []);

    // Handle edit input blur
    const handleEditBlur = useCallback(() => {
        const trimmedValue = editValue.trim();
        if (trimmedValue === '') {
            if (node?.inputEle && onCancelNewNode) {
                onCancelNewNode();
            } else {
                setEditError(translate('ABB.Powertrain.Frontend.locationNameRequiredErrorMessage'));
            }
            return;
        }
        if (!VALID_TEXT_REGEX.test(trimmedValue)) {
            setEditError(translate('ABB.Powertrain.Frontend.locationNameSpecialCharErrorMessage'));
            return;
        }
        if (onSaveNewNode) {
            onSaveNewNode(trimmedValue, node, setEditError);
        }

    }, [editValue, onSaveNewNode, onCancelNewNode, node]);

    // Handle edit input key down
    const handleEditKeyDown = useCallback((e) => {
        e.stopPropagation(); // Prevent event from bubbling up to parent components

        if (e.key === 'Enter') {
            e.preventDefault();
            handleEditBlur();
        } else if (e.key === 'Escape') {
            e.preventDefault();
            setEditValue(node.name || '');
            setEditError(null);
            // Handle canceling new input element node
            if (onCancelNewNode) {
                onCancelNewNode();
            }
        }
        // Allow all other keys (including space) to work normally in the input
    }, [handleEditBlur, node.name, onCancelNewNode]);

    // Handle drop position calculation on mouse enter
    const handleDropPositionMove = (e) => {
        if (!isOver || !onSetDropPosition) {
            return;
        }

        if ([NODE_TYPE.POWERTRAIN, NODE_TYPE.ASSET].includes(draggedItems?.[0].type)) {
            onSetDropPosition(DROP_POSITION.INSIDE);
            return;
        }

        const rect = e.currentTarget.getBoundingClientRect();
        const mouseY = e.clientY;
        const offsetY = mouseY - rect.top;
        const height = rect.height;
        const heightPercent = offsetY / height;

        // Top 50% = BEFORE, Bottom 25% = AFTER, Middle 25% = INSIDE
        if (heightPercent < DROP_THRESHOLD.BEFORE) {
            onSetDropPosition(DROP_POSITION.BEFORE);
        } else if (heightPercent > DROP_THRESHOLD.AFTER) {
            onSetDropPosition(DROP_POSITION.AFTER);
        } else {
            onSetDropPosition(DROP_POSITION.INSIDE);
        }

    };

    // Render expand/collapse icon
    const renderExpandIcon = useMemo(() => {
        if (!showExpandIcon) { return null; }

        if (canExpand) {
            return (
                <button
                    className={`hierarchy-node-expand-button ${isExpanded ? 'collapse' : 'expand'}`}
                    onClick={handleExpandToggle}
                    aria-label={isExpanded ? 'Collapse' : 'Expand'}
                    aria-expanded={isExpanded}
                >
                    {isExpanded ? <IconDropDownMo /> : <IconChevronRight />}
                </button>
            );
        }

        return <span className='hierarchy-node-expand-spacer' />;
    }, [showExpandIcon, canExpand, isExpanded, handleExpandToggle]);

    // Render checkbox
    const renderCheckbox = useMemo(() => {
        if (!showCheckbox || !selectable) { return null; }

        return (
            <Checkbox
                checked={isSelected}
                width={'16px'}
                height={'16px'}
            />
        );
    }, [showCheckbox, selectable, isSelected]);

    // Render inline editing input
    const renderEditInput = useMemo(() => {
        if (!isEditing && !node.inputEle) {
            return null;
        }

        return (
            <div className='hierarchy-node-edit-input-wrapper'>
                <input
                    ref={inputRef}
                    type='text'
                    className={`hierarchy-node-edit-input ${editError ? 'hierarchy-node-edit-input-error' : ''}`}
                    value={editValue}
                    onChange={handleEditChange}
                    onBlur={handleEditBlur}
                    onKeyDown={handleEditKeyDown}
                    onKeyUp={(e) => e.stopPropagation()}
                    onClick={(e) => e.stopPropagation()}
                    placeholder={node.placeholder}
                    maxLength={MAX_NODE_NAME_LENGTH}
                />
                {editError &&
                    <Tippy
                        content={editError}
                        interactive={true}
                        interactiveBorder={10}
                        animation='scale'
                        theme='light-border'
                        trigger='mouseenter'
                        showOnCreate={true}
                        placement='right'
                        maxWidth={500}
                    >
                        <div className={'error-icon'}>
                            <IconCondition condition={3} />
                        </div>
                    </Tippy>
                }
            </div >
        );
    }, [isEditing, node.inputEle, editValue, editError, handleEditChange, handleEditBlur, handleEditKeyDown]);

    // Render custom node content
    const customNodeContent = useMemo(() => {
        if (renderNode) {
            return renderNode(node, {
                isSelected,
                isExpanded,
                level,
                isDragging: isDragging || isSortableDragging,
                isOver,
                isEditing,
                editMode,
            });
        }
        return null;
    }, [renderNode, node, isSelected, isExpanded, level, isDragging, isSortableDragging, isOver, isEditing, editMode]);

    // Render drag handle (edit mode)
    const renderDragHandleIcon = useMemo(() => {
        if (!editMode || !showDragHandle || !isDraggable) {
            return null;
        }

        return (
            <span className='hierarchy-node-drag-handle-icon'>
                <IconDrag />
            </span>
        );
    }, [editMode, showDragHandle, isDraggable]);

    // Calculate indent style
    const indentStyle = useMemo(() => ({
        paddingLeft: `${level * indent}px`,
    }), [level, indent]);

    // Combine styles
    const combinedStyle = useMemo(() => ({
        ...style,
        ...sortableStyle,
    }), [style, sortableStyle]);

    // CSS classes
    const nodeClasses = useMemo(() => {
        const classes = ['hierarchy-node'];
        if (isSelected) { classes.push('hierarchy-node-selected'); }
        if (isDragging || isSortableDragging) { classes.push('hierarchy-node-dragging'); }
        if (isOver && dropPosition === DROP_POSITION.INSIDE) { classes.push('hierarchy-node-over'); }
        if (!selectable) { classes.push('hierarchy-node-disabled'); }
        if (selectable) { classes.push('hierarchy-node-selectable'); }
        if (node.disabled) { classes.push('hierarchy-node-disabled'); }
        if (hasChildren) { classes.push('hierarchy-node-has-children'); }
        if (isExpanded) { classes.push('hierarchy-node-expanded'); }
        if (editMode) { classes.push('hierarchy-node-edit-mode'); }
        if (isEditing) { classes.push('hierarchy-node-editing'); }
        if (node.isVirtual) { classes.push('hierarchy-node-virtual'); }
        return classes.join(' ');
    }, [isSelected, isDragging, isSortableDragging, isOver, dropPosition, selectable, node.disabled, node.isVirtual, hasChildren, isExpanded, editMode, isEditing]);

    return (
        <div className='hierarchy-node-wrapper'>
            <button
                type='button'
                ref={setNodeRef}
                className={nodeClasses}
                style={combinedStyle}
                onClick={handleClick}
                onContextMenu={handleContextMenu}
                aria-pressed={selectable ? isSelected : undefined}
                aria-disabled={node.disabled}
            >
                {/* Drop indicator - before */}
                {isOver && dropPosition === DROP_POSITION.BEFORE && draggedItems?.[0].type === NODE_TYPE.LOCATION &&
                    <div
                        className='hierarchy-drop-indicator hierarchy-drop-indicator-line hierarchy-drop-indicator-before'
                    />
                }
                <div
                    className={`hierarchy-node-content-wrapper${node.disabled ? ' hierarchy-node-disabled' : ''}`}
                    style={indentStyle}
                    data-id={nodeKey}
                    onMouseMove={handleDropPositionMove}
                >
                    {/* Expand/collapse icon */}
                    {renderExpandIcon}

                    {/* Draggable content area */}
                    <div className='hierarchy-node-drag-handle' {...(isDraggable ? { ...attributes, ...listeners } : {})}>
                        {/* Drag handle icon */}
                        {renderDragHandleIcon}

                        {/* Checkbox */}
                        {renderCheckbox}

                        {/* Node icon */}
                        {renderIcon(node)}

                        {/* Inline editing or content */}
                        {isEditing || node.inputEle
                            ? renderEditInput
                            : customNodeContent ||
                            <HierarchyNodeContent
                                node={node}
                                renderActions={renderActions}
                                isOver={isOver}
                                childNodeCount={childNodeCount}
                                showDragPreview={showDragPreview}
                            />
                        }
                    </div>
                </div>
                {/* Drop indicator - after */}
                {isOver && dropPosition === DROP_POSITION.AFTER && draggedItems?.[0].type === NODE_TYPE.LOCATION &&
                    <div
                        className='hierarchy-drop-indicator hierarchy-drop-indicator-line hierarchy-drop-indicator-after'
                    />
                }
            </button>
        </div>
    );
});

HierarchyNode.propTypes = {
    node: PropTypes.object.isRequired,
    level: PropTypes.number.isRequired,
    isSelected: PropTypes.bool,
    isExpanded: PropTypes.bool,
    isDragging: PropTypes.bool,
    isOver: PropTypes.bool,
    dropPosition: PropTypes.oneOf([DROP_POSITION.BEFORE, DROP_POSITION.INSIDE, DROP_POSITION.AFTER]),
    indent: PropTypes.number,
    showExpandIcon: PropTypes.bool,
    showCheckbox: PropTypes.bool,
    onToggleExpand: PropTypes.func.isRequired,
    onNodeClick: PropTypes.func.isRequired,
    onContextMenu: PropTypes.func,
    renderNode: PropTypes.func,
    renderIcon: PropTypes.func,
    renderActions: PropTypes.func,
    dragEnabled: PropTypes.bool,
    getNodeKey: PropTypes.func.isRequired,
    isNodeSelectable: PropTypes.func.isRequired,
    style: PropTypes.object,
    editMode: PropTypes.bool,
    isEditing: PropTypes.bool,
    showDragHandle: PropTypes.bool,
    onSaveNewNode: PropTypes.func,
    onCancelNewNode: PropTypes.func,
    onSetDropPosition: PropTypes.func,
    draggedItems: PropTypes.array,
    childNodeCount: PropTypes.number,
    showDragPreview: PropTypes.bool,
};

HierarchyNode.defaultProps = {
    isSelected: false,
    isExpanded: false,
    isDragging: false,
    isOver: false,
    dropPosition: DROP_POSITION.INSIDE,
    indent: 20,
    showExpandIcon: true,
    showCheckbox: false,
    onContextMenu: null,
    renderNode: null,
    renderIcon: null,
    renderActions: null,
    dragEnabled: false,
    style: {},
    editMode: false,
    isEditing: false,
    showDragHandle: true,
    onSaveNewNode: null,
    onCancelNewNode: null,
    onSetDropPosition: null,
    draggedItems: null,
    childNodeCount: 0,
    showDragPreview: false,
};

HierarchyNode.displayName = 'HierarchyNode';

export default HierarchyNode;
